import React from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  Sheet,
  Chip,
  Avatar,
  Stack,
  LinearProgress,
  Divider,
} from '@mui/joy'
import Grid from '@mui/joy/Grid'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { TrendUp as TrendingUp, TrendDown as TrendingDown, PersonSimpleWalk as DirectionsWalk, Trophy as EmojiEvents, Target as TrackChanges, Check, Warning } from '@phosphor-icons/react'
import { useSalesCanvasing } from '../../../hooks/useSalesReports'
import type { DateRange, DailyKickoffCanvasser, GoalProgressEntry } from '../../../types/salesReports'

interface CanvasingMetricsProps {
  dateRange: DateRange
}

const getInitials = (name: string) => {
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

export default function CanvasingMetrics({ dateRange }: CanvasingMetricsProps) {
  const { data, isLoading, error } = useSalesCanvasing({
    date_from: dateRange.from,
    date_to: dateRange.to,
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    // Check if it's a "module not available" error
    const errorData = error as any
    if (errorData?.coming_soon) {
      return (
        <Alert color="neutral" startDecorator={<DirectionsWalk />}>
          <Box>
            <Typography level="title-md">Canvasing Module Coming Soon</Typography>
            <Typography level="body-sm">
              The canvasing module is not yet available. Once enabled, you'll see door-to-door activity metrics here.
            </Typography>
          </Box>
        </Alert>
      )
    }

    return (
      <Alert color="danger">
        Failed to load canvasing data. Please try again.
      </Alert>
    )
  }

  if (!data) {
    return (
      <Alert color="warning">
        No canvasing data available for the selected period.
      </Alert>
    )
  }

  const { metrics, comparisons, by_canvasser, weekly_trend, daily_kickoff, goal_progress } = data

  const renderChange = (change: number | null) => {
    if (change === null) {
      return null  // Hide chip when no comparison data
    }
    const isPositive = change > 0
    const Icon = isPositive ? TrendingUp : TrendingDown
    const color = isPositive ? 'success' : 'danger'

    return (
      <Chip
        size="sm"
        variant="soft"
        color={color}
        startDecorator={<Icon size={14} />}
      >
        {isPositive ? '+' : ''}{change.toFixed(1)}%
      </Chip>
    )
  }

  // Calculate team average for daily kickoff coloring
  const teamAvgKnocks = daily_kickoff?.by_canvasser.length > 0
    ? daily_kickoff.team_totals.knocks / daily_kickoff.by_canvasser.length
    : 0

  // Helper to get progress bar color based on pace
  const getProgressColor = (entry: GoalProgressEntry): 'success' | 'primary' | 'warning' | 'danger' => {
    if (entry.percentage >= 100) return 'success'
    if (entry.on_track) return 'primary'
    const expectedPercentage = (entry.expected_pace / entry.target) * 100
    if (entry.percentage >= expectedPercentage * 0.7) return 'warning'
    return 'danger'
  }

  return (
    <Box>
      {/* Daily Kickoff Section */}
      {daily_kickoff && daily_kickoff.by_canvasser.length > 0 && (
        <Card variant="outlined" sx={{ mb: 4, borderLeft: 4, borderLeftColor: 'primary.500' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Box>
                <Typography level="h4" startDecorator={<EmojiEvents color="var(--joy-palette-warning-400)" />}>
                  Daily Kickoff - {daily_kickoff.yesterday_day_name}'s Results
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Performance from {new Date(daily_kickoff.yesterday_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </Typography>
              </Box>
              <Stack direction="row" spacing={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography level="h3" sx={{ color: 'var(--brand-text, #7013f5)' }}>{daily_kickoff.team_totals.knocks}</Typography>
                  <Typography level="body-xs">Total Knocks</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography level="h3" sx={{ color: 'success.500' }}>{daily_kickoff.team_totals.contacts}</Typography>
                  <Typography level="body-xs">Answers</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography level="h3" sx={{ color: 'neutral.500' }}>
                    {teamAvgKnocks.toFixed(0)}
                  </Typography>
                  <Typography level="body-xs">Team Avg</Typography>
                </Box>
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              {daily_kickoff.by_canvasser.map((canvasser: DailyKickoffCanvasser, index: number) => {
                const aboveAvg = canvasser.yesterday_knocks >= teamAvgKnocks
                return (
                  <Grid xs={12} sm={6} md={4} lg={3} key={canvasser.user_id}>
                    <Card
                      variant="soft"
                      sx={{
                        borderLeft: 4,
                        borderLeftColor: aboveAvg ? 'success.500' : 'danger.400',
                        position: 'relative',
                      }}
                    >
                      {index === 0 && (
                        <Chip
                          size="sm"
                          variant="solid"
                          color="warning"
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        >
                          Top Performer
                        </Chip>
                      )}
                      <CardContent>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                          <Avatar size="sm">{getInitials(canvasser.name)}</Avatar>
                          <Box>
                            <Typography level="title-sm">{canvasser.name}</Typography>
                            {canvasser.target_knocks_per_week > 0 && (
                              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                Target: {canvasser.target_knocks_per_week}/wk
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                        <Typography level="h2" sx={{ color: aboveAvg ? 'success.600' : 'danger.500', mb: 1 }}>
                          {canvasser.yesterday_knocks}
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Answers</Typography>
                            <Typography level="body-md" fontWeight="lg">{canvasser.yesterday_contacts}</Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Convos</Typography>
                            <Typography level="body-md" fontWeight="lg">{canvasser.yesterday_interested}</Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Follow-ups</Typography>
                            <Typography level="body-md" fontWeight="lg">{canvasser.yesterday_followups}</Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          <Chip size="sm" variant="outlined">
                            {canvasser.contact_rate}% Answer
                          </Chip>
                          <Chip size="sm" variant="outlined">
                            {canvasser.interest_rate}% Convo
                          </Chip>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Goal Progress Section */}
      {goal_progress && (goal_progress.week.by_canvasser.length > 0 || goal_progress.month.by_canvasser.length > 0) && (
        <Card variant="outlined" sx={{ mb: 4, borderLeft: 4, borderLeftColor: 'success.500' }}>
          <CardContent>
            <Typography level="h4" sx={{ mb: 3 }} startDecorator={<TrackChanges color="var(--joy-palette-success-500)" />}>
              Goal Progress
            </Typography>

            <Grid container spacing={3}>
              {/* Weekly Progress */}
              <Grid xs={12} md={6}>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  This Week (Day {goal_progress.week.days_elapsed} of 5)
                </Typography>
                {goal_progress.week.by_canvasser.length > 0 ? (
                  <Stack spacing={2}>
                    {goal_progress.week.by_canvasser.map((entry: GoalProgressEntry) => (
                      <Box key={entry.user_id}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography level="body-sm" fontWeight="lg">{entry.name}</Typography>
                            <Chip
                              size="sm"
                              variant="soft"
                              color={entry.on_track ? 'success' : 'warning'}
                              startDecorator={entry.on_track ? <Check size={12} /> : <Warning size={12} />}
                            >
                              {entry.on_track ? 'On Track' : 'Behind'}
                            </Chip>
                          </Stack>
                          <Typography level="body-sm">
                            {entry.current} / {entry.target} ({entry.percentage}%)
                          </Typography>
                        </Stack>
                        <Box sx={{ position: 'relative' }}>
                          <LinearProgress
                            determinate
                            value={Math.min(entry.percentage, 100)}
                            color={getProgressColor(entry)}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                          {/* Expected pace marker */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: `${Math.min((entry.expected_pace / entry.target) * 100, 100)}%`,
                              top: 0,
                              bottom: 0,
                              width: 2,
                              bgcolor: 'neutral.600',
                              transform: 'translateX(-50%)',
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    No users with weekly targets set
                  </Typography>
                )}
              </Grid>

              {/* Monthly Progress */}
              <Grid xs={12} md={6}>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  {goal_progress.month.month_name} (Day {goal_progress.month.days_elapsed})
                </Typography>
                {goal_progress.month.by_canvasser.length > 0 ? (
                  <Stack spacing={2}>
                    {goal_progress.month.by_canvasser.map((entry: GoalProgressEntry) => (
                      <Box key={entry.user_id}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography level="body-sm" fontWeight="lg">{entry.name}</Typography>
                            <Chip
                              size="sm"
                              variant="soft"
                              color={entry.on_track ? 'success' : 'warning'}
                              startDecorator={entry.on_track ? <Check size={12} /> : <Warning size={12} />}
                            >
                              {entry.on_track ? 'On Track' : 'Behind'}
                            </Chip>
                          </Stack>
                          <Typography level="body-sm">
                            {entry.current} / {entry.target} ({entry.percentage}%)
                          </Typography>
                        </Stack>
                        <Box sx={{ position: 'relative' }}>
                          <LinearProgress
                            determinate
                            value={Math.min(entry.percentage, 100)}
                            color={getProgressColor(entry)}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                          {/* Expected pace marker */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: `${Math.min((entry.expected_pace / entry.target) * 100, 100)}%`,
                              top: 0,
                              bottom: 0,
                              width: 2,
                              bgcolor: 'neutral.600',
                              transform: 'translateX(-50%)',
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    No users with weekly targets set
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography level="title-lg" sx={{ mb: 3 }}>
        Canvasing Activity
      </Typography>

      {/* Summary Metrics */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" color="primary">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Total Knocks</Typography>
              <Typography level="h3">{metrics.total_knocks}</Typography>
              {renderChange(comparisons.vs_previous_period.knocks_change)}
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" color="success">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Answers</Typography>
              <Typography level="h3">{metrics.contacts_made}</Typography>
              {renderChange(comparisons.vs_previous_period.contacts_change)}
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" color="warning">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Conversations</Typography>
              <Typography level="h3">{metrics.interested}</Typography>
              {renderChange(comparisons.vs_previous_period.interested_change)}
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" sx={{ backgroundColor: '#F3E5F5' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Inspections</Typography>
              <Typography level="h3">{metrics.inspection}</Typography>
              {renderChange(comparisons.vs_previous_period.inspection_change)}
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" sx={{ backgroundColor: '#E8F5E9' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Insp. Scheduled</Typography>
              <Typography level="h3">{metrics.inspections_scheduled}</Typography>
              {renderChange(comparisons.vs_previous_period.inspections_scheduled_change)}
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" sx={{ backgroundColor: '#FFF3E0' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Left Info</Typography>
              <Typography level="h3">{metrics.left_info}</Typography>
              {renderChange(comparisons.vs_previous_period.left_info_change)}
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" sx={{ backgroundColor: 'rgba(123,31,162,0.12)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs" sx={{ color: '#7B1FA2' }}>Mkt. Drops</Typography>
              <Typography level="h3" sx={{ color: '#7B1FA2' }}>{metrics.marketing_drops}</Typography>
              {renderChange(comparisons.vs_previous_period.marketing_drops_change)}
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" color="neutral">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Follow-ups</Typography>
              <Typography level="h3">{metrics.followups_required}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" color="primary">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Contact Rate</Typography>
              <Typography level="h3">{metrics.contact_rate}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={4} md={2}>
          <Card variant="soft" color="success">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Interest Rate</Typography>
              <Typography level="h3">{metrics.interest_rate}%</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Weekly Trend Chart */}
        <Grid xs={12} lg={8}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Weekly Activity Trend
              </Typography>
              {weekly_trend.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={weekly_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="week"
                      tickFormatter={(val) => val ? new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(val) => val ? new Date(val as string).toLocaleDateString() : ''}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="knocks" stroke="#0B6BCB" strokeWidth={2} name="Knocks" />
                    <Line type="monotone" dataKey="contacts" stroke="#06A77D" strokeWidth={2} name="Answers" />
                    <Line type="monotone" dataKey="interested" stroke="#F9A825" strokeWidth={2} name="Conversations" />
                    <Line type="monotone" dataKey="inspection" stroke="#9C27B0" strokeWidth={2} name="Inspections" />
                    <Line type="monotone" dataKey="inspections_scheduled" stroke="#4CAF50" strokeWidth={2} name="Insp. Scheduled" />
                    <Line type="monotone" dataKey="left_info" stroke="#FF9800" strokeWidth={2} name="Left Info" />
                    <Line type="monotone" dataKey="marketing_drops" stroke="#9C27B0" strokeWidth={2} name="Mkt. Drops" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Alert color="neutral">No trend data available</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Previous Period Comparison */}
        <Grid xs={12} lg={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Previous Period
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Knocks
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="h4">{comparisons.previous_period.total_knocks}</Typography>
                    {renderChange(comparisons.vs_previous_period.knocks_change)}
                  </Stack>
                </Box>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Answers
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="h4">{comparisons.previous_period.contacts_made}</Typography>
                    {renderChange(comparisons.vs_previous_period.contacts_change)}
                  </Stack>
                </Box>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Conversations
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="h4">{comparisons.previous_period.interested}</Typography>
                    {renderChange(comparisons.vs_previous_period.interested_change)}
                  </Stack>
                </Box>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Inspections
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="h4">{comparisons.previous_period.inspection}</Typography>
                    {renderChange(comparisons.vs_previous_period.inspection_change)}
                  </Stack>
                </Box>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Insp. Scheduled
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="h4">{comparisons.previous_period.inspections_scheduled}</Typography>
                    {renderChange(comparisons.vs_previous_period.inspections_scheduled_change)}
                  </Stack>
                </Box>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Left Info
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="h4">{comparisons.previous_period.left_info}</Typography>
                    {renderChange(comparisons.vs_previous_period.left_info_change)}
                  </Stack>
                </Box>
                <Box>
                  <Typography level="body-sm" sx={{ color: '#7B1FA2' }}>
                    Mkt. Drops
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="h4">{comparisons.previous_period.marketing_drops}</Typography>
                    {renderChange(comparisons.vs_previous_period.marketing_drops_change)}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Canvasser Performance */}
      {by_canvasser.length > 0 && (
        <>
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Canvasser Performance Comparison
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={by_canvasser.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tickFormatter={(val) => val.split(' ')[0]} />
                  <YAxis />
                  <Tooltip
                    itemSorter={(item) => {
                      const order = ['knocks', 'contacts_made', 'interested', 'inspection', 'inspections_scheduled', 'left_info', 'marketing_drops']
                      return order.indexOf(item.dataKey as string)
                    }}
                  />
                  <Legend />
                  <Bar dataKey="knocks" fill="#0B6BCB" name="Knocks" />
                  <Bar dataKey="contacts_made" fill="#06A77D" name="Answers" />
                  <Bar dataKey="interested" fill="#F9A825" name="Conversations" />
                  <Bar dataKey="inspection" fill="#9C27B0" name="Inspections" />
                  <Bar dataKey="inspections_scheduled" fill="#4CAF50" name="Insp. Scheduled" />
                  <Bar dataKey="left_info" fill="#FF9800" name="Left Info" />
                  <Bar dataKey="marketing_drops" fill="#9C27B0" name="Mkt. Drops" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Canvasser Details
              </Typography>
              <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table>
                  <thead>
                    <tr>
                      <th>Canvasser</th>
                      <th style={{ textAlign: 'right' }}>Knocks</th>
                      <th style={{ textAlign: 'right' }}>Answers</th>
                      <th style={{ textAlign: 'right' }}>Convos</th>
                      <th style={{ textAlign: 'right' }}>Insp.</th>
                      <th style={{ textAlign: 'right' }}>Scheduled</th>
                      <th style={{ textAlign: 'right' }}>Left Info</th>
                      <th style={{ textAlign: 'right' }}>Drops</th>
                      <th style={{ textAlign: 'right' }}>Follow-ups</th>
                      <th style={{ textAlign: 'right' }}>Ans. Rate</th>
                      <th style={{ textAlign: 'right' }}>Conv. Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {by_canvasser.map((canvasser) => (
                      <tr key={canvasser.user_id}>
                        <td>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar size="sm">{getInitials(canvasser.name)}</Avatar>
                            <Typography level="body-sm" fontWeight="lg">
                              {canvasser.name}
                            </Typography>
                          </Stack>
                        </td>
                        <td style={{ textAlign: 'right' }}>{canvasser.knocks}</td>
                        <td style={{ textAlign: 'right' }}>{canvasser.contacts_made}</td>
                        <td style={{ textAlign: 'right' }}>{canvasser.interested}</td>
                        <td style={{ textAlign: 'right' }}>{canvasser.inspection}</td>
                        <td style={{ textAlign: 'right' }}>{canvasser.inspections_scheduled}</td>
                        <td style={{ textAlign: 'right' }}>{canvasser.left_info}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Typography level="body-sm" sx={{ color: '#7B1FA2' }}>{canvasser.marketing_drops}</Typography>
                        </td>
                        <td style={{ textAlign: 'right' }}>{canvasser.followups_required}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={canvasser.contact_rate >= 50 ? 'success' : canvasser.contact_rate >= 25 ? 'warning' : 'neutral'}
                          >
                            {canvasser.contact_rate}%
                          </Chip>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={canvasser.interest_rate >= 30 ? 'success' : canvasser.interest_rate >= 15 ? 'warning' : 'neutral'}
                          >
                            {canvasser.interest_rate}%
                          </Chip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  )
}
